import { StackActions, createNavigationContainerRef } from "@react-navigation/native";

export const navigationRef = createNavigationContainerRef<any>();
let currentRouteName: string | null = null;
const routeNameListeners = new Set<() => void>();
const EMPTY_ROUTE_SNAPSHOT = {
  name: null,
  params: null,
} as const;
let currentRouteSnapshot: { name: string | null; params: Record<string, any> | null } = {
  name: null,
  params: null,
};
const routeSnapshotListeners = new Set<() => void>();

export const rootNavigate = (name: string, params?: Record<string, any>) => {
  if (!navigationRef.isReady()) return;

  if (params !== undefined) {
    navigationRef.navigate(name, params);
    return;
  }

  navigationRef.navigate(name);
};

export const rootReplace = (name: string, params?: Record<string, any>) => {
  if (!navigationRef.isReady()) return;

  navigationRef.dispatch(StackActions.replace(name, params));
};

export const getCurrentRouteName = () => {
  if (currentRouteName !== null) return currentRouteName;
  if (!navigationRef.isReady()) return null;
  return navigationRef.getCurrentRoute()?.name ?? null;
};

export const getCurrentRouteSnapshot = () => {
  if (currentRouteSnapshot.name !== null || currentRouteSnapshot.params !== null) {
    return currentRouteSnapshot;
  }
  if (!navigationRef.isReady()) return EMPTY_ROUTE_SNAPSHOT;

  const route = navigationRef.getCurrentRoute();
  if (!route) {
    return EMPTY_ROUTE_SNAPSHOT;
  }

  return {
    name: route?.name ?? null,
    params: (route?.params as Record<string, any> | undefined) ?? null,
  };
};

export const setCurrentRouteName = (routeName: string | null) => {
  if (currentRouteName === routeName) return;
  currentRouteName = routeName;
  routeNameListeners.forEach((listener) => listener());
};

export const setCurrentRouteSnapshot = (
  route: { name?: string | null; params?: Record<string, any> | null } | null
) => {
  const nextName = route?.name ?? null;
  const nextParams = route?.params ?? null;
  const hasSameName = currentRouteSnapshot.name === nextName;
  const hasSameParams = JSON.stringify(currentRouteSnapshot.params) === JSON.stringify(nextParams);

  if (hasSameName && hasSameParams) {
    return;
  }

  currentRouteSnapshot = {
    name: nextName,
    params: nextParams,
  };

  setCurrentRouteName(nextName);

  routeSnapshotListeners.forEach((listener) => listener());
};

export const subscribeRouteName = (listener: () => void) => {
  routeNameListeners.add(listener);

  return () => {
    routeNameListeners.delete(listener);
  };
};

export const subscribeRouteSnapshot = (listener: () => void) => {
  routeSnapshotListeners.add(listener);

  return () => {
    routeSnapshotListeners.delete(listener);
  };
};
